import { useEffect, useMemo, useState } from 'react'

import Loader from '@/common/components/Loader'
import { PageTitle } from '@/components'
import { Modal } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import {
  LeaderboardStandingEntryDto,
  LeaderboardStandingsDto,
} from '@/common/models/gamification'
import { getLeaderboardStandings } from '@/core/services/gamification'

interface StandingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Board whose live standings are fetched when the modal opens. */
  boardId: string | null
}

/**
 * Read-only view of a leaderboard's live standings. Standings are a snapshot computed by the
 * gamification service — if the admin just edited a custom board's window, they must click
 * Regenerate on the list page before the new standings show up here.
 */
export const StandingsModal = ({
  open,
  onOpenChange,
  boardId,
}: StandingsModalProps) => {
  const [standings, setStandings] = useState<LeaderboardStandingsDto | null>(
    null,
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !boardId) {
      setStandings(null)
      setError(null)
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const { data } = await getLeaderboardStandings(boardId, 200)
        if (!cancelled) setStandings(data)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load standings')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, boardId])

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Rank',
        accessor: 'rank',
        render: (row: LeaderboardStandingEntryDto) => (
          <span className="text-monochrome-1">{row.rank + 1}</span>
        ),
      },
      {
        title: 'User',
        accessor: 'displayName',
        render: (row: LeaderboardStandingEntryDto) => (
          <span className="text-monochrome-1">
            {row.displayName || row.userId}
          </span>
        ),
      },
      {
        title: 'Rank Tier',
        accessor: 'roadmapRank',
        render: (row: LeaderboardStandingEntryDto) =>
          row.roadmapRank ? (
            <div className="flex items-center gap-2">
              {row.roadmapRank.imageRef && (
                <img
                  src={row.roadmapRank.imageRef}
                  alt=""
                  width={24}
                  height={24}
                  className="rounded-[8px] border border-monochrome-120 object-contain"
                  style={{ width: 24, height: 24 }}
                />
              )}
              <span className="text-monochrome-1">
                {row.roadmapRank.name || '—'}
              </span>
            </div>
          ) : (
            <span className="text-monochrome-2">—</span>
          ),
      },
      {
        title: 'Score',
        accessor: 'score',
        render: (row: LeaderboardStandingEntryDto) => (
          <span className="text-monochrome-2">{row.score}</span>
        ),
      },
    ],
    [],
  )

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={
        <div className="border-b border-monochrome-120 pb-4">
          <PageTitle
            icon="events-star"
            title={
              <span className="flex items-center gap-2">
                Standings{' '}
                {standings?.board?.name && (
                  <span className="text-accent-1">{standings.board.name}</span>
                )}
              </span>
            }
          />
        </div>
      }
    >
      {loading && <Loader />}
      {error && <div className="mb-4 text-body-medium text-accent-3">{error}</div>}
      <div className="mb-4 text-xs text-monochrome-2">
        {standings?.board?.kind === 'DEFAULT' ? (
          <>
            Standings are maintained automatically by the award engine. Use
            Rebuild on the list page only to recover from data loss or
            backfill.
          </>
        ) : (
          <>
            Active custom leaderboards update live as points are awarded.
            Editing the window takes effect within about a minute — or click
            Regenerate on the list page to apply it instantly. A newly created
            or reactivated board should be regenerated once to backfill earlier
            awards.
          </>
        )}
      </div>
      <Table
        className="[&_th]:!whitespace-nowrap"
        columns={columns}
        data={standings?.entries ?? []}
        noDataMessage="No standings yet"
      />
    </Modal>
  )
}
